<template>
  <div class="DataExtensionsList--Extension" v-loading="isLoading">
    <form-section
      :title="extensionName"
      :description="$t('Imports_Title_DataMapping_Description')"
    >
      <template slot="controls">
        <el-button type="danger" @click="removeDataExtension">
          {{ $t('Integrations_Salesforce_DataMapping_RemoveDataExtension') }}
        </el-button>
        <el-button type="primary" @click="updateAttributes">
          {{ $t('Integrations_Salesforce_DataMapping_UpdateAttributes') }}
        </el-button>
        <rest-form-item
          v-show="!columns.some(e => e.attribute)"
          prop="columns"
        />
      </template>

      <el-table :data="columns" :stripe="true">
        <el-table-column
          :label="$t('Integrations_Salesforce_Mapping_attributeName_Column')"
        >
          <template slot-scope="scope">
            <div>
              {{ columns[scope.$index]['attributeName'] }}
            </div>
          </template>
        </el-table-column>

        <el-table-column :label="$t('Imports_Label_MapTo')">
          <template slot-scope="scope">
            <div v-if="columns[scope.$index].readOnly || false">
              {{ columns[scope.$index].attribute }}
            </div>
            <el-select
              v-model="columns[scope.$index].attribute"
              :id="columns[scope.$index].attribute + '--Input'"
              :placeholder="$t(placeholder)"
              filterable
              remote
              :remote-method="fetchAttributesRemote"
              @visible-change="selectVisibleChange"
            >
              <el-option :value="null">
                {{ $t(placeholder) }}
              </el-option>
              <el-option-group
                v-if="standardAttributes && standardAttributes.length > 0"
                :label="$t('Imports_Text_SystemAttributes')"
              >
                <el-option
                  v-for="(item, index) in standardAttributes"
                  :key="index"
                  :label="item"
                  :value="item"
                  :disabled="columnsIncludes(item)"
                />
              </el-option-group>

              <el-option-group
                :label="$t('Imports_Text_CustomAttributes')"
                v-if="attributes.length > 0"
              >
                <el-option
                  v-for="(item, index) in attributes"
                  :key="index"
                  :label="item.name"
                  :value="item.name"
                  :disabled="columnsIncludes(item.name)"
                />
              </el-option-group>
            </el-select>
            <p
              v-if="
                showError(
                  columns[scope.$index].attribute,
                  columns[scope.$index]['dataExtensionName']
                ) && showUnrecognizedError
              "
              class="error-field"
            >
              {{ getErrorField(scope) }}
              {{ $t('Imports_Text_AttributeNotRecognized') }}
            </p>
          </template>
        </el-table-column>
      </el-table>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import { projectEndpoint, customConfirm } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import { findIndex, unionBy, cloneDeep, clone } from 'lodash'
export default {
  name: 'DataExtensionMapping',

  components: {
    FormSection,
    RestFormItem
  },

  props: {
    model: {
      type: Object
    },

    extensionName: {
      type: String,
      required: true
    },

    placeholder: {
      type: String,
      required: false,
      default: 'Imports_Text_DontImport'
    },

    showUnrecognizedError: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isLoading: false,
      attributes: [],
      columns: [],
      primaryKeys: [],
      defaultCustomAttr: [],
      search: '',
      filterableAttributes: [
        'email',
        'mobile_number',
        'whatsapp_number',
        'language',
        'timezone',
        'email_addressable',
        'email_subscription',
        'sms_addressable',
        'sms_subscription'
      ]
    }
  },

  mounted() {
    this.updateAttributes().then(() => {
      this.populateInitialData()
      this.fetchAttributesRemote()
    })
  },

  computed: {
    extensionFetchEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `integrations/salesforce/get-attributes`,
        data: {
          ...this.model.params,
          extensionName: this.extensionName
        }
      })
    },

    contactID() {
      return this.model.params.contact_id
    },

    standardAttributes() {
      let filtered = clone(this.filterableAttributes)
      if (this.$store.state.app.features.customerIdEnabled) {
        if (this.contactID !== 'customer_id') {
          filtered.unshift('customer_id')
        }
        if (this.contactID === 'customer_id') {
          filtered.unshift('user_id')
        }
      }
      if (this.search !== '') {
        return filtered.filter(e => e.includes(this.search))
      }

      return filtered
    },

    attributesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `tags`
      })
    }
  },

  watch: {
    columns: {
      deep: true,
      handler: function(newValue) {
        this.$emit('mappingChanged', newValue)
      }
    }
  },

  methods: {
    updateAttributes() {
      return new Promise((resolve, reject) => {
        this.isLoading = true
        RestApi(this.extensionFetchEndpoint)
          .then(({ data }) => {
            this.primaryKeys = data.columns.filter(item => {
              return !!item.primaryKey
            })

            this.model.params.primaryKeys = unionBy(
              this.model.params.primaryKeys,
              this.primaryKeys,
              'fqn'
            )

            data.columns = data.columns.filter(item => {
              return !item.primaryKey
            })

            data.columns.forEach(column => {
              const attributeID = column.id

              const attributeForUpdating = findIndex(
                this.model.params.mapping,
                item => item.id && item.id === attributeID
              )

              if (
                attributeForUpdating > -1 &&
                this.model.params.mapping[attributeForUpdating].fqn &&
                this.model.params.mapping[attributeForUpdating].fqn !==
                  column.fqn
              ) {
                this.model.params.mapping[attributeForUpdating] = {
                  ...this.model.params.mapping[attributeForUpdating],
                  ...column
                }
              }
            })

            this.columns = unionBy(
              cloneDeep(
                (this.model.params.mapping || []).filter(
                  item =>
                    item.dataExtensionName === this.extensionName &&
                    !item.primaryKey
                )
              ),
              data.columns,
              'fqn'
            )

            this.isLoading = false
            resolve(data)
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            const extensionIndex = findIndex(
              this.model.params.enabledDataExtensions,
              extension => extension.dataExtensionName === this.extensionName
            )
            if (extensionIndex > -1) {
              this.model.params.enabledDataExtensions.splice(extensionIndex, 1)
            }

            this.isLoading = false
            reject(error)
          })
      })
    },

    removeDataExtension() {
      customConfirm(
        this.$t('Integrations_Salesforce_DataMapping_RemoveDE_Title'),
        this.$t('Integrations_Salesforce_DataMapping_RemoveDE_Message'),
        this.$t('Integrations_Salesforce_DataMapping_RemoveDE_Confirm'),
        undefined,
        undefined,
        ' custom-confirm-ce'
      )
        .then(_ => this.$emit('removeDataExtension', this.extensionName))
        .catch(_ => {})
    },

    showError(attr, field) {
      return (
        attr === undefined &&
        !this.filterableAttributes.includes(field) &&
        !this.attributes.map(item => item.name).includes(field)
      )
    },

    getErrorField(scope) {
      return [
        this.columns[scope.$index]['dataExtensionName'],
        this.columns[scope.$index]['attributeName']
      ].join('.')
    },

    populateInitialData() {
      this.columns.forEach(column => {
        if (!column.hasOwnProperty('attribute')) {
          const attributeIndex = this.filterableAttributes.findIndex(
            item =>
              column.attributeName.replaceAll(' ', '_').toLowerCase() ===
              item.replaceAll(' ', '_').toLowerCase()
          )
          if (attributeIndex !== -1) {
            this.$set(
              column,
              'attribute',
              this.filterableAttributes[attributeIndex]
            )
          }
        }
      })
    },

    fetchAttr(attr) {
      return RestApi({
        ...this.attributesEndpoint,
        params: {
          title: attr
        }
      })
    },

    selectVisibleChange() {
      this.attributes = this.defaultCustomAttr
      this.search = ''
    },

    fetchAttributesRemote(query) {
      this.search = query
      let params = { sort: 'title', attribute_profile: 1 }
      if (query !== null && query !== '') {
        params.title = query
      }

      RestApi({
        ...this.attributesEndpoint,
        params: params
      })
        .then(({ data }) => {
          if (this.defaultCustomAttr.length === 0) {
            this.defaultCustomAttr = data
          }
          this.attributes = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    columnsIncludes(attributeName) {
      return this.columns.some(column => column.attribute === attributeName)
    },

    translate(column) {
      if (
        this.translations &&
        this.translations.hasOwnProperty(column) &&
        this.translations[column]
      ) {
        return this.$t(this.translations[column])
      }
      return column
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/types/salesforce/DataExtensionMapping.vue